/**
 * WP9a / HG4 settings navigation for the verification shell.
 *
 * Visible launch groups: User, Workspace, Appearance, Integrations, Agents,
 * Advanced. Multiplayer is visible because team/device sync pages are mounted
 * (Devices, Primary & sync). Apps, Customers, and Personal are compatibility
 * (direct-route) — not primary IA unless re-approved.
 *
 * Leaf hrefs come from `buildSettingsNav()` so WP9d can generate the retained
 * route list without a second source of truth. This module does not repair
 * connection/repo detail (WP9b/c) and does not rewrite leaves.
 */

import {
  APPS_SETTINGS_GROUP,
  CUSTOMERS_SETTINGS_GROUP,
  PERSONAL_SETTINGS_GROUP,
  buildSettingsNav,
  type SettingsNavGroup,
  type SettingsNavItem,
} from "@excellent/core/settings-tree";

/** HG4 primary launch groups (buyer-facing). */
export const LAUNCH_SETTINGS_KEYS = [
  "user",
  "workspace",
  "appearance",
  "integrations",
  "agents",
  "advanced",
] as const;

/** HG4: Multiplayer is visible while team/device sync pages remain live. */
export const MULTIPLAYER_SETTINGS_KEY = "team";

export type SettingsLandingKind = "launch" | "compatibility";

export type SettingsLandingGroup = SettingsNavGroup & {
  kind: SettingsLandingKind;
};

function railGroupToLanding(
  key: string,
  iconKey: string,
  group: { label: string; pages: { href: string; label: string; activeHref?: string }[] },
  kind: SettingsLandingKind,
): SettingsLandingGroup {
  return {
    key,
    label: group.label,
    iconKey,
    href: group.pages[0]?.href ?? "/settings",
    consolidated: false,
    items: group.pages.map((p) => ({
      href: p.href,
      label: p.label,
      activeHref: p.activeHref,
    })),
    kind,
  };
}

function withKind(group: SettingsNavGroup, kind: SettingsLandingKind): SettingsLandingGroup {
  return { ...group, kind };
}

/** Primary HG4 groups shown on the verification settings landing. */
export function launchSettingsGroups(): SettingsLandingGroup[] {
  const nav = buildSettingsNav();
  const byKey = new Map(nav.map((g) => [g.key, g]));
  const out: SettingsLandingGroup[] = [];
  for (const key of LAUNCH_SETTINGS_KEYS) {
    const group = byKey.get(key);
    if (group) out.push(withKind(group, "launch"));
  }
  const multiplayer = byKey.get(MULTIPLAYER_SETTINGS_KEY);
  if (multiplayer) out.push(withKind(multiplayer, "launch"));
  return out;
}

/**
 * HG4 compatibility groups: Apps, Customers, Personal.
 * Reachable by direct route; not primary launch IA.
 */
export function compatibilitySettingsGroups(): SettingsLandingGroup[] {
  const nav = buildSettingsNav();
  const apps = nav.find((g) => g.key === "apps");
  const out: SettingsLandingGroup[] = [];
  if (apps) out.push(withKind(apps, "compatibility"));
  else out.push(railGroupToLanding("apps", "AppWindow", APPS_SETTINGS_GROUP, "compatibility"));
  out.push(railGroupToLanding("customers", "Contact", CUSTOMERS_SETTINGS_GROUP, "compatibility"));
  out.push(railGroupToLanding("personal", "Package", PERSONAL_SETTINGS_GROUP, "compatibility"));
  return out;
}

export function settingsLandingGroups(): SettingsLandingGroup[] {
  return [...launchSettingsGroups(), ...compatibilitySettingsGroups()];
}

/** Strip in-page anchors so landing links open the full page (heavy leaves). */
export function settingsPageHref(item: SettingsNavItem | string): string {
  const href = typeof item === "string" ? item : item.href;
  return href.split("#")[0] || href;
}
